/**
 * FleshCash Affiliate Tracker
 *
 * Persists affiliate data in cookies and syncs it to Shopify cart attributes
 * so order webhooks can handle attribution server-side.
 */
(() => {
  if (window.__ilfFleshCashAffiliateTrackerLoaded) return;
  window.__ilfFleshCashAffiliateTrackerLoaded = true;

  /* ── Config ────────────────────────────────────────────────── */
  const COOKIE_DAYS = 60;
  const MAX_VALUE_LENGTH = 128;
  const VALUE_ALLOWLIST = /[^a-zA-Z0-9_.-]/g;

  const COOKIES = {
    ref: 'fc_aff',
    click: 'fc_click',
    sub: 'fc_sub',
  };

  const PARAM_ALIASES = {
    ref: ['ref', 'aff', 'affid'],
    click: ['fc_click'],
    sub: ['subid', 'sub_id', 'sub1'],
  };

  // Phase 1: current keys (compatible with existing webhook).
  const CART_KEYS = {
    ref: 'ref',
    click: 'fc_click',
    sub: 'subid',
  };
  // Phase 2: uncomment once webhook supports hidden underscore keys.
  // const CART_KEYS = { ref: '__ref', click: '__fc_click', sub: '__subid' };

  /* ── Helpers ───────────────────────────────────────────────── */
  const sanitize = (value) => {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed) return null;

    const cleaned = trimmed
      .slice(0, MAX_VALUE_LENGTH)
      .replace(VALUE_ALLOWLIST, '');

    return cleaned || null;
  };

  const setCookie = (name, value, days) => {
    const expiresAt = new Date();
    expiresAt.setTime(expiresAt.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = [
      `${name}=${encodeURIComponent(value)}`,
      `expires=${expiresAt.toUTCString()}`,
      'path=/',
      'SameSite=Lax',
      'Secure',
    ].join(';');
  };

  const getCookie = (name) => {
    const prefix = `${name}=`;
    const cookie = document.cookie
      .split(';')
      .map((entry) => entry.trim())
      .find((entry) => entry.startsWith(prefix));

    if (!cookie) return null;
    return decodeURIComponent(cookie.slice(prefix.length));
  };

  const readFirstParam = (params, aliases) => {
    for (const alias of aliases) {
      const value = sanitize(params.get(alias));
      if (value) return value;
    }
    return null;
  };

  const normalizeAttributeValue = (value) => (
    value == null
      ? ''
      : String(value)
  );

  /* ── Core ──────────────────────────────────────────────────── */
  const resolveTrackingData = () => {
    const params = new URLSearchParams(window.location.search);

    const urlValues = {
      ref: readFirstParam(params, PARAM_ALIASES.ref),
      click: readFirstParam(params, PARAM_ALIASES.click),
      sub: readFirstParam(params, PARAM_ALIASES.sub),
    };

    let cookieValues = {
      ref: sanitize(getCookie(COOKIES.ref)),
      click: sanitize(getCookie(COOKIES.click)),
      sub: sanitize(getCookie(COOKIES.sub)),
    };

    if (urlValues.ref) {
      setCookie(COOKIES.ref, urlValues.ref, COOKIE_DAYS);
      cookieValues.ref = urlValues.ref;
    }

    if (urlValues.click) {
      setCookie(COOKIES.click, urlValues.click, COOKIE_DAYS);
      cookieValues.click = urlValues.click;
    }

    if (urlValues.sub) {
      setCookie(COOKIES.sub, urlValues.sub, COOKIE_DAYS);
      cookieValues.sub = urlValues.sub;
    }

    return cookieValues;
  };

  const buildCartAttributes = (trackingData) => {
    const attributes = {};

    if (trackingData.ref) attributes[CART_KEYS.ref] = trackingData.ref;
    if (trackingData.click) attributes[CART_KEYS.click] = trackingData.click;
    if (trackingData.sub) attributes[CART_KEYS.sub] = trackingData.sub;

    return attributes;
  };

  const syncCartAttributes = async (trackingData) => {
    const nextAttributes = buildCartAttributes(trackingData);
    if (!Object.keys(nextAttributes).length) return;

    try {
      const cartResponse = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!cartResponse.ok) return;

      const cart = await cartResponse.json();
      const existingAttributes = cart?.attributes ?? {};
      // console.log('[ILF FleshCash] Current Shopify cart attributes:', existingAttributes);
      const hasChanges = Object.entries(nextAttributes).some(
        ([key, value]) => normalizeAttributeValue(existingAttributes[key]) !== value
      );

      if (!hasChanges) return;

      const updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ attributes: nextAttributes }),
      });

      if (!updateResponse.ok) return;
    } catch (_error) {
      // Best-effort sync: attribution should never interrupt storefront behavior.
    }
  };

  /* ── Extensibility Hook ────────────────────────────────────── */
  const onAffiliateVisit = (_trackingData) => {
    // Reserved for optional pageview/click integrations in the future.
  };

  /* ── Entry Point ───────────────────────────────────────────── */
  const init = async () => {
    const trackingData = resolveTrackingData();
    if (!trackingData.ref && !trackingData.click && !trackingData.sub) return;

    onAffiliateVisit(trackingData);
    await syncCartAttributes(trackingData);
  };

  void init();
})();
